import { Component, inject, Inject, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { applyFormValidation } from '../helper-functions/docusign-validator';
import { TEMPLATE_NAMES, APPLICANT_TYPE, ENVELOPE_ROLES } from '../constants/docusign-constants';
import { DocusignEnvelopeRecipients } from '../interface/docusign-envelope-recipients-interface';
import { DocusignEnvelopeContents } from '../interface/docusign-envelope-contents-interface';
import { DocusignEnvelope } from '../interface/docusign-envelope-interface';
import { DocusignEnvelopeDetails } from '../interface/docusign-envelope-details-interface';
import { addEnvelopeRecipients } from '../helper-functions/docusign-utils';

@Component({
  selector: 'app-edit-docusign-envelope',
  templateUrl: './edit-docusign-envelope.component.html'
})
export class EditDocusignEnvelopeComponent implements OnInit {
  private readonly _loaderSheet = inject(MatBottomSheet);
  readonly _templateName = TEMPLATE_NAMES;   // constant
  readonly _appType = APPLICANT_TYPE;    // constant
  readonly _envelopeRole = ENVELOPE_ROLES;    // constant
  envelopeContentForm = new FormGroup({
    envelopeOwnerName: new FormControl(''),
    isCoApp: new FormControl(''),
    co_applicant_firstname: new FormControl(''),
    co_applicant_lastname: new FormControl(''),
    SOBFullName: new FormControl(''),
    SOBRelationship: new FormControl(''),
    email: new FormControl('',[
      Validators.required,
      Validators.email, 
      Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$") // Strict standard format
    ]),
    co_applicant_email: new FormControl('',[
      Validators.required,
      Validators.email, 
      Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$") // Strict standard format
    ])
  });
  
  dbTemplateName: string = '';
  templateName: string = '';
  envelopeData:any;
  parentEnvelopeData:any;
  subEnvelopeData:any;

  docusignEnvelopeRecipients: Partial<DocusignEnvelopeRecipients> | undefined;   // Defined Envelope Recipients
  docusignEnvelopeContents: Partial<DocusignEnvelopeContents> | undefined;   // Defined Envelope Contents
  docusignEnvelope: Partial<DocusignEnvelope> | undefined;   // Defined Envelope Main
  docusignEnvelopeDetails: Partial<DocusignEnvelopeDetails> | undefined;   // Defined Envelope Sub-details
  docusignZohoData: any;
  
  constructor(
    private _sheetRef: MatBottomSheetRef<EditDocusignEnvelopeComponent>,
    private _docusignService: DocusignService,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any) {
  }
  ngOnInit(): void {
    if(this.data){
      this.dbTemplateName = this.data?.templateRowId?.template_name?.toLowerCase();
      this.setupConditionalValidation(this.dbTemplateName);
      this.templateName = this.data?.templateRowId?.template_name?.replaceAll('_', ' ');
      const isCoApp = (this.data?.applicant_type?.toLowerCase() === 'primary') ? 'No' : 'Yes';
      const envelopeOwnerName = (this.dbTemplateName === this._templateName?.CONSENT)
                                ? this.data?.name
                                : this.data?.applicant_type?.toLowerCase() === this._appType?.PRIMARY
                                ? this.data?.envelopeDetails?.main_applicant_name
                                : this.data?.envelopeDetails?.co_applicant_firstname?.concat(' ')?.concat(this.data?.envelopeDetails?.co_applicant_lastname);
      this.envelopeContentForm.patchValue({
        envelopeOwnerName: envelopeOwnerName,
        isCoApp: isCoApp,
        co_applicant_firstname: this.data?.envelopeDetails?.co_applicant_firstname,
        co_applicant_lastname: this.data?.envelopeDetails?.co_applicant_lastname,
        SOBFullName: this.data?.envelopeDetails?.speak_on_behalf_name,
        SOBRelationship: this.data?.envelopeDetails?.speak_on_behalf_relationship,
        email: this.data?.client_email,
        co_applicant_email: (this.dbTemplateName === this._templateName?.AOB) ? this.data?.envelopeDetails?.co_applicant_email : '', 
      });

      const primarySOB = ['SOBFullName','SOBRelationship','email'];
      const coAppSOB = ['SOBFullName','SOBRelationship','email'];
      const primaryConsent = ['envelopeOwnerName','email'];
      const coAppConsent =  ['co_applicant_firstname','co_applicant_lastname','email'];
      const lppCancellation = ['envelopeOwnerName','email'];
      const aobForm = ['envelopeOwnerName','email','co_applicant_firstname','co_applicant_lastname','co_applicant_email'];

      if(this.dbTemplateName?.toLowerCase() === this._templateName?.CONSENT){
        if(this.data?.applicant_type?.toLowerCase() === this._appType?.PRIMARY){
          applyFormValidation(this.envelopeContentForm, primaryConsent);
        }else{
          applyFormValidation(this.envelopeContentForm, coAppConsent);
        }
      } 

      if(this.dbTemplateName?.toLowerCase() === this._templateName?.SOB){
        if(this.data?.applicant_type?.toLowerCase() === this._appType?.PRIMARY){
          applyFormValidation(this.envelopeContentForm, primarySOB);
        }else{
          applyFormValidation(this.envelopeContentForm, coAppSOB);
        }
      }

      if(this.dbTemplateName?.toLowerCase() === this._templateName?.LPPCANCELLATION){
          applyFormValidation(this.envelopeContentForm, lppCancellation);
      } 

      if(this.dbTemplateName?.toLowerCase() === this._templateName?.AOB){
          applyFormValidation(this.envelopeContentForm, aobForm);
      } 
    }
  }
  
  onSubmit() {
    this.mapDocusignEnvelopeRecipients(this.envelopeContentForm?.value, this._envelopeRole?.CLIENT, "1");
    const mergedSobEnvelopeRecipients = [];
    addEnvelopeRecipients(mergedSobEnvelopeRecipients, this.docusignEnvelopeRecipients );
    this.mapDocusignEnvelopeContents(this.data?.applicant_type?.toLowerCase(),this.envelopeContentForm?.value);
    this.mapDocusignEnvelope(this.data?.applicant_type?.toLowerCase(),this.envelopeContentForm?.value);
    this.mapDocusignEnvelopeDetails(this.data?.applicant_type?.toLowerCase(), this.envelopeContentForm?.value);
    this.mapZohoData(this.data?.applicant_type?.toLowerCase(), this.envelopeContentForm?.value);
    const formattedEnvelopeData = { 
      ...{docusign_res: {envelope_id: this.data?.envelope_id, status: this.data?.statusId?.status_name}},
      ...{selectedTemplate: {
          templateType: this.dbTemplateName?.toLowerCase(),
          applicant_type: this.data?.applicant_type?.toLowerCase(),          
        }
      },
      ...{envelopeRecipients: mergedSobEnvelopeRecipients},
      ...{envelopeContents: this.docusignEnvelopeContents},
      ...{dbMainEnvelope: this.docusignEnvelope},
      ...{dbSubEnvelopeDetails: this.docusignEnvelopeDetails},
      ...{zohoDataUpdate: this.docusignZohoData},
    };

    this._sheetRef.dismiss(formattedEnvelopeData);
  }

  mapDocusignEnvelopeRecipients(envelopeForm:any, roleName:string, recipientId:string){
    const envelopeOwnerName = this.data?.applicant_type?.toLowerCase() === this._appType?.PRIMARY
                                ? envelopeForm?.envelopeOwnerName
                                : envelopeForm?.co_applicant_firstname?.concat(' ')?.concat(envelopeForm?.co_applicant_lastname);
    this.docusignEnvelopeRecipients = {
      name:envelopeOwnerName?.toUpperCase(),
      email: envelopeForm?.email,
      roleName: roleName,
      recipientId: recipientId,
    };
  }

  mapDocusignEnvelopeContents(applicantType:string, envelopeForm:any){
    this.docusignEnvelopeContents = {
      isCoApp: (applicantType?.toLowerCase() === this._appType?.PRIMARY) ? 'No' : 'Yes',
      DealName: (applicantType?.toLowerCase() === this._appType?.PRIMARY) ? envelopeForm?.envelopeOwnerName?.toUpperCase() : this.data?.envelopeDetails?.main_applicant_name?.toUpperCase(),
      CoSignerName: envelopeForm?.co_applicant_firstname?.concat(' ')?.concat(envelopeForm?.co_applicant_lastname)?.toUpperCase(),
      SOBRelationship: envelopeForm?.['SOBRelationship']?.toLowerCase(),
      SOBFullName: envelopeForm?.['SOBFullName']?.toUpperCase()
    };
  }

  mapDocusignEnvelope(applicantType:string, envelopeForm:any){    
    const dbDisplayName = (this.dbTemplateName === this._templateName?.SOB) 
                          ? envelopeForm?.SOBFullName?.toUpperCase() 
                          : (applicantType?.toLowerCase() === this._appType?.PRIMARY) 
                          ? envelopeForm?.envelopeOwnerName?.toUpperCase() 
                          : envelopeForm?.co_applicant_firstname?.concat(' ')?.concat(envelopeForm?.co_applicant_lastname)?.toUpperCase();
    this.docusignEnvelope = {
      application_id: this.data?.application_id,
      applicant_type: applicantType?.toUpperCase(),
      template_name: this.dbTemplateName?.toLowerCase(),
      name: dbDisplayName?.toUpperCase(),
      client_email: envelopeForm?.email
    };
  }

  mapDocusignEnvelopeDetails(applicantType:string, envelopeForm:any){
    this.docusignEnvelopeDetails = {
      application_id: this.data?.application_id,
      main_applicant_name: (applicantType?.toLowerCase() === this._appType?.PRIMARY) ? envelopeForm?.envelopeOwnerName?.toUpperCase() : this.data?.envelopeDetails?.main_applicant_name?.toUpperCase(),
      main_applicant_email: (applicantType?.toLowerCase() === this._appType?.PRIMARY) ? envelopeForm?.email : this.data?.envelopeDetails?.main_applicant_email,
      co_applicant_firstname: envelopeForm?.co_applicant_firstname?.toUpperCase(),
      co_applicant_lastname: envelopeForm?.co_applicant_lastname?.toUpperCase(),
      co_applicant_email: (this.dbTemplateName === this._templateName?.AOB) ? envelopeForm?.co_applicant_email : (applicantType?.toLowerCase() === this._appType?.COAPP) ? envelopeForm?.email  : this.data?.envelopeDetails?.co_applicant_email,   
      speak_on_behalf_zoho_id: this.data?.envelopeDetails?.speak_on_behalf_zoho_id,
      speak_on_behalf_name: envelopeForm?.['SOBFullName']?.toUpperCase(),
      speak_on_behalf_relationship: envelopeForm?.['SOBRelationship']?.toLowerCase(),
      work_drive_id: this.data?.envelopeDetails?.work_drive_id,      
    };
  }

  mapZohoData(applicantType:string, envelopeForm:any){
    this.docusignZohoData = {
      application_id: this.data?.application_id,
      isCoApp: (applicantType?.toLowerCase() === this._appType?.PRIMARY) ? 'No' : 'Yes',
      co_applicant_firstname: envelopeForm?.co_applicant_firstname?.toUpperCase(),
      co_applicant_lastname: envelopeForm?.co_applicant_lastname?.toUpperCase(),
      email: envelopeForm?.email,
      DealName: (applicantType?.toLowerCase() === this._appType?.PRIMARY) ? envelopeForm?.envelopeOwnerName?.toUpperCase() : this.data?.envelopeDetails?.main_applicant_name?.toUpperCase(),
      templateName: this.dbTemplateName?.toLowerCase(),
      sobZohoId: this.data?.envelopeDetails?.speak_on_behalf_zoho_id,
      SOBFullName: envelopeForm?.['SOBFullName']?.toUpperCase(),
      SOBRelationship: envelopeForm?.['SOBRelationship']?.toUpperCase(),
    }
  }

  cancel() {
    this._sheetRef.dismiss();
  }

  setupConditionalValidation(templateName:string) {
    const relationship = this.envelopeContentForm.get('SOBRelationship');
    const sobName = this.envelopeContentForm.get('SOBFullName');
    if(templateName?.toLowerCase() === this._templateName?.SOB){
      relationship?.setValidators([Validators.required]);
      sobName?.setValidators([Validators.required]);
    }else{
      relationship?.clearValidators();
      sobName?.setValidators([Validators.required]);
    }
    relationship?.updateValueAndValidity();
    sobName?.updateValueAndValidity();
  }


}
